import { useRef, useCallback } from "react";

export function useFormattedInput(formatFn?: (v: string) => string) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Use for user typing (preserve caret)
  const formatOnUserChange = useCallback((rawValue: string) => {
    if (!formatFn){
      return rawValue;
    } 
    const el = inputRef.current;
    if (!el) {
      return formatFn(rawValue);
    }

    const caret: number = el.selectionStart || 0 as number;
    const digitsBefore = rawValue.slice(0, caret).replace(/\D/g, "").length;

    const formatted = formatFn(rawValue);

    // Restore caret after React paints
    requestAnimationFrame(() => {
      const node = inputRef.current;
      let i = 0, count = 0;
      while (i < formatted.length && count < digitsBefore) {
        if (/\d/.test(formatted[i])) count++;
        i++;
      }
      node?.setSelectionRange(i, i);
    });

    return formatted;
  }, [formatFn]);

  // Use for programmatic updates (no caret work)
  const formatProgrammatic = useCallback((rawValue: string) => {
    return formatFn ? formatFn(rawValue) : rawValue;
  }, [formatFn]);

  return { inputRef, formatOnUserChange, formatProgrammatic };
}